import dayjs from 'dayjs';
import { useState, useEffect } from 'react';

interface CalendarProps {
  selectedDate: any;
  onSelectDate: (date: any) => void;
  label?: string;
  minDate?: any;
}

export default function Calendar({
  selectedDate,
  onSelectDate,
  label = 'Date',
  minDate,
}: CalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(dayjs(selectedDate));

  // Sync when selectedDate changes externally
  useEffect(() => {
    if (!currentMonth.isSame(selectedDate, 'month')) {
      setCurrentMonth(dayjs(selectedDate));
    }
  }, [selectedDate]);

  const startOfMonth = currentMonth.startOf('month');
  const endOfMonth = currentMonth.endOf('month');
  const startDay = startOfMonth.day(); // Sunday = 0
  const weeks = [];

  // Start from the Sunday before the first of the month
  let currentDay = startOfMonth.subtract(startDay, 'day');

  // Build exactly 5 weeks (5 * 7 = 35 days)
  for (let w = 0; w < 5; w++) {
    const week = [];
    for (let d = 0; d < 7; d++) {
      week.push(currentDay);
      currentDay = currentDay.add(1, 'day');
    }
    weeks.push(week);
  }

  return (
    <div className="p-2 rounded-lg text-white w-fit">
      <h3 className="pb-2 border-b border-frame-stroke mb-2">{label}</h3>

      {/* Header */}
      <div className="flex justify-between items-center mb-2 bg-modal-input p-[6px]">
        <div
          className="bg-navigation-tab-active p-[2px] rounded-md cursor-pointer"
          onClick={() => setCurrentMonth(currentMonth.subtract(1, 'month'))}
        >
          <Arrow />
        </div>
        <span className="font-semibold">
          {currentMonth.format('MMMM YYYY')}
        </span>
        <div
          className="bg-navigation-tab-active p-[2px] rounded-md cursor-pointer rotate-180"
          onClick={() => setCurrentMonth(currentMonth.add(1, 'month'))}
        >
          <Arrow />
        </div>
      </div>

      {/* Weekdays */}
      <div className="grid grid-cols-7 text-center text-[#A3A3A3] text-sm mb-1 gap-2">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
          <div
            className="uppercase  text-center w-9 h-9 p-2 rounded-md transition-colors bg-[#121212]"
            key={d}
          >
            {d}
          </div>
        ))}
      </div>

      {/* Days */}
      <div className="grid grid-cols-7 text-center gap-2">
        {weeks.map((week, wi) =>
          week.map((date, di) => {
            const isSelected = selectedDate.isSame(date, 'day');
            const isCurrentMonth = date.isSame(currentMonth, 'month');
            const isDisabled = minDate && date.isBefore(minDate, 'day');

            return (
              <div
                key={date.format('DD-MM-YYYY')}
                onClick={() => !isDisabled && onSelectDate(date)}
                className={`p-2 rounded-md w-9 h-9 transition-colors bg-[#121212] ${
                  isDisabled
                    ? 'text-[#3e3e3e]/70 cursor-not-allowed opacity-70'
                    : isSelected
                      ? 'bg-primary text-text-primary-heading font-semibold cursor-pointer'
                      : isCurrentMonth
                        ? 'hover:opacity-50 cursor-pointer'
                        : 'text-[#3e3e3e] hover:bg-[#121212]/60 cursor-pointer'
                }`}
              >
                {date.date()}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function Arrow() {
  return (
    <svg
      width="21"
      height="21"
      viewBox="0 0 21 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9.70401 10.285L13.4165 13.9975L12.356 15.058L7.58301 10.285L12.356 5.51196L13.4165 6.57246L9.70401 10.285Z"
        fill="#7A7A7A"
      />
    </svg>
  );
}
